import { init as initApm } from '@elastic/apm-rum'
import { ApmRoutes } from '@elastic/apm-rum-react'
import { useEffect } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { HashRouter as Router, Route, useLocation } from 'react-router-dom'

import * as NetworkErrorDialogSlice from 'slices/networkErrorDialogSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { getTenant, selectTenantsStatus } from 'slices/tenantsSlice'
import { ERROR_STATUS_CODE } from 'slices/utils'

import ErrorBoundary from 'components/ErrorDialog/ErrorBoundary'
import { NetworkErrorDialog } from 'components/ErrorDialog/NetworkErrorDialog'
import SessionTimeoutDialog from 'components/ErrorDialog/SessionTimeoutDialog'
import Header from 'components/Header/Header'
import NotFoundPage from 'components/NotFoundPage/NotFoundPage'
import Spinner from 'components/Spinner/Spinner'

import useAuthority from 'hooks/useAuthority'
import { useIpRestrictedRedirect } from 'hooks/useIpRestrictedRedirect'
import { useTechtouch } from 'hooks/useTechtouch'

import { noHeaderPages, headerPages } from './pages'

const apm = initApm({
  serviceName: 'sbc-pc',
  serverUrl: process.env.REACT_APP_APM_SERVER,
  serviceVersion: '',
  environment: process.env.REACT_APP_ENVIRONMENT,
})

const App = () => {
  const { loggedIn, activated, team, user } = useSelector(selectSessionStatus, shallowEqual)
  const { tenant } = useSelector(selectTenantsStatus, shallowEqual)
  const { isTenantAdmin } = useAuthority()
  useTechtouch(tenant, user)
  const dispatch = useDispatch()

  useEffect(() => {
    apm.setCustomContext({ team, user })
  }, [team, user])

  useEffect(() => {
    activated && loggedIn && !isTenantAdmin && dispatch(getTenant(user.userHasTenants[0].id))
  }, [dispatch, user, isTenantAdmin, loggedIn, activated])

  return (
    <Router>
      <RouterContent />
    </Router>
  )
}

const RouterContent = () => {
  useIpRestrictedRedirect()
  const dispatch = useDispatch()
  const location = useLocation()

  const handleRenderError = (error: Error) => {
    dispatch(
      NetworkErrorDialogSlice.open({
        code: ERROR_STATUS_CODE.UNKNOWN,
        errorMessage: error.message,
        responseMessage: error.stack || error.message,
        requestTime: new Date().toLocaleString('ja-JP'),
      })
    )
  }

  return (
    <>
      <Spinner />
      <NetworkErrorDialog />
      <SessionTimeoutDialog />
      <Header />
      <ErrorBoundary key={`${location.pathname}${location.search}`} onError={handleRenderError}>
        <ApmRoutes>
          {[...noHeaderPages, ...headerPages].map(page => (
            <Route key={page.path} path={page.path} element={<page.component />} />
          ))}
          <Route path="*" Component={NotFoundPage} />
        </ApmRoutes>
      </ErrorBoundary>
    </>
  )
}

export default App
